import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, Calendar, Mail, Users, Globe, CreditCard, BarChart3 } from 'lucide-react'
import { APP_URL } from '@/lib/public-trpc'
import { capability, SELF_SERVE_PACKAGES } from '@gjl/voice/pricing'

const features = [
  {
    icon: Globe,
    title: 'Website Builder',
    description: 'Build a professional website without coding. Multi-page, mobile-responsive, custom domains.',
    href: '/platform/features/website',
  },
  {
    icon: Calendar,
    title: 'Event Management',
    description: 'Create beautiful event pages with RSVP, calendar integration, and automatic reminders.',
    href: '/platform/features/events',
  },
  {
    icon: Mail,
    title: 'Email Newsletters',
    description: 'Send updates to your audience. Rich text editor, scheduling, and automated sequences.',
    href: '/platform/features/email',
  },
  {
    icon: CreditCard,
    title: 'Online Donations',
    description: 'Accept donations with Stripe. One-time or recurring. No platform fees.',
    href: '/platform/features/donations',
  },
  {
    icon: Users,
    title: 'People Management',
    description: 'Showcase musicians, staff, and board members with professional profiles.',
    href: '/platform/features/team',
  },
  {
    icon: BarChart3,
    title: 'Analytics',
    description: "Track website visits, email opens, and event attendance. Know what's working.",
    href: '/platform/features/analytics',
  },
]

// Prices render from @gjl/voice/pricing. Edit them there.
const website = capability('website')!
const complete = SELF_SERVE_PACKAGES.find((p) => p.id === 'complete')!

const tiers = [
  {
    name: 'Website',
    price: `$${website.priceMonthly}`,
    period: '/month',
    description: 'The foundation: your site, run by you',
    features: [
      'Website builder with unlimited pages',
      'Custom domain',
      'Forms and SEO built in',
      'Team roles and people profiles',
      'Analytics included',
      'Accepting donations is free (0% platform fee)',
      'Month to month, no contracts',
    ],
    cta: 'Get Started',
    ctaHref: '/get-started',
    highlighted: false,
  },
  {
    name: complete.name,
    price: `$${complete.priceMonthly}`,
    period: '/month',
    annualPrice: 'Pay annually and get 2 months free',
    description: 'Website, email, donations, and ticketing on one plan',
    features: [
      'Everything in Website',
      'Email marketing (up to 2,000 contacts)',
      'Donor management: recurring giving, tax receipts, donor records',
      'Ticketing with QR check-in (plus 1.5% per ticket)',
      'Donor management and ticketing set up for you by our team',
      'Priority support',
    ],
    cta: 'See All Pricing',
    ctaHref: '/packages',
    highlighted: true,
  },
]

const audiences = [
  'Orchestras & Symphonies',
  'Choral Organizations',
  'Chamber Ensembles',
  'Music Festivals',
  'Opera Companies',
  'Church Music Programs',
  'Teaching Studios',
  'Early Music Groups',
]

export default function PlatformPage() {
  useEffect(() => {
    document.title = 'Fugue Lab App - All-in-One Tools for Music Organizations'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const audiencesRef = useScrollReveal<HTMLDivElement>('.audience-tag', { stagger: 0.05 })
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const comparisonRef = useStaggeredGrid<HTMLDivElement>('.comparison-card', { stagger: 0.15 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            The Fugue Lab App: self-serve at fuguelab.app
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            Your music organization,
            <br />
            one platform
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Event pages, email newsletters, donations, and audience management without juggling five different tools.
            From ${website.priceMonthly} a month. Run it yourself, or have us run it for you.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/get-started">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </Link>
            <Link to="/packages">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  View Pricing
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">Month to month. No contracts.</p>
        </div>
      </section>

      {/* Who It's For */}
      <section className="bg-muted/30 px-4 py-16">
        <div ref={audiencesRef} className="container mx-auto max-w-5xl">
          <h2 className="mb-8 text-center text-2xl font-bold">Built for Music Organizations</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {audiences.map((audience) => (
              <span
                key={audience}
                className="audience-tag bg-background border-border rounded-full border px-4 py-2 text-sm"
              >
                {audience}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Everything You Need</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Stop paying for separate website builders, email platforms, and donation tools. Get it all in one place.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="feature-card border-border bg-card hover:border-primary group block rounded-lg border p-6 transition-colors"
                >
                  <feature.icon className="text-primary mb-4 h-10 w-10" />
                  <h3 className="group-hover:text-primary mb-2 text-lg font-semibold transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                  <span className="text-primary mt-4 inline-block text-sm">Learn more &rarr;</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
            <p className="text-muted-foreground">
              One flat monthly price per capability. Turn on more as your organization grows.
            </p>
          </div>
          <div ref={pricingRef} className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
            {tiers.map((tier) => (
              <motion.div
                key={tier.name}
                className="pricing-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div
                  className={`h-full rounded-2xl border p-8 ${
                    tier.highlighted ? 'bg-primary/5 border-primary/50' : 'bg-card border-border'
                  }`}
                >
                  {tier.highlighted && <div className="text-primary mb-4 text-sm font-medium">Most Popular</div>}
                  <div className="mb-6">
                    <h3 className="mb-2 text-2xl font-bold">{tier.name}</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">{tier.price}</span>
                      {tier.period && <span className="text-muted-foreground">{tier.period}</span>}
                    </div>
                    {tier.annualPrice && <p className="text-primary mt-1 text-sm">{tier.annualPrice}</p>}
                    <p className="text-muted-foreground mt-2">{tier.description}</p>
                  </div>

                  <ul className="mb-8 space-y-3">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={tier.ctaHref} className="block">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    >
                      <Button
                        className={`w-full cursor-pointer ${tier.highlighted ? '' : 'variant-outline'}`}
                        variant={tier.highlighted ? 'default' : 'outline'}
                      >
                        {tier.cta}
                      </Button>
                    </motion.div>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App vs Studio */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">App vs. Studio</h2>
            <p className="text-muted-foreground">
              One platform, two ways to run it: the App is you at the controls, the Studio is us.
            </p>
          </div>
          <div ref={comparisonRef} className="grid gap-8 md:grid-cols-2">
            <motion.div
              className="comparison-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="mb-2 text-xl font-bold">Fugue Lab App</h3>
              <p className="text-primary mb-4 text-sm">Self-serve - fuguelab.app</p>
              <p className="text-muted-foreground mb-4">
                Do it yourself with our tools. Create event pages, send newsletters, collect donations. Accepting
                donations is always free.
              </p>
              <ul className="text-muted-foreground mb-6 space-y-2 text-sm">
                <li>- Perfect for hands-on directors</li>
                <li>- Flat monthly pricing per capability</li>
                <li>- Full control over everything</li>
              </ul>
              <a href={`${APP_URL}/auth`}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">Get Started</Button>
                </motion.div>
              </a>
            </motion.div>
            <motion.div
              className="comparison-card border-border bg-card rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="mb-2 text-xl font-bold">Fugue Lab Studio</h3>
              <p className="text-muted-foreground mb-4 text-sm">Done-for-you - fuguelab.com</p>
              <p className="text-muted-foreground mb-4">
                We build it for you. Custom websites, digital systems, ongoing support. Premium service.
              </p>
              <ul className="text-muted-foreground mb-6 space-y-2 text-sm">
                <li>- Perfect for busy organizations</li>
                <li>- Custom design & development</li>
                <li>- White-glove onboarding</li>
              </ul>
              <Link to="/packages">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button variant="outline" className="w-full cursor-pointer">
                    View Studio Packages
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to simplify?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to grow their audience.
            </p>
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Create Your Account <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
